// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Loading, Space, Styled, Icon} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {useNavigationDOM, useResponsive, useToast, MutationError} from '@supermove/hooks';
import {Project, ProjectModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import Checkbox from '@shared/design/components/Checkbox';
import Drawer from '@shared/design/components/Drawer';
import Line from '@shared/design/components/Line';
import Text from '@shared/design/components/Text';
import TextTooltip from '@shared/design/components/TextTooltip';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import DuplicateProjectForm, {
  DuplicateProjectFormType,
} from '@shared/modules/Project/forms/DuplicateProjectForm';
import useDuplicateProjectMutation from '@shared/modules/Project/hooks/useDuplicateProjectMutation';
import DuplicateProjectClientFields from 'modules/Project/V2/Show/components/DuplicateProjectClientFields';

const Container = Styled.View<{index: number}>`
  z-index: ${({index = 0}) => 100 - index};
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const TooltipContainer = Styled.View`
`;

const SubheadingWithTooltip = ({
  subheadingText,
  tooltipText,
}: {
  subheadingText: string;
  tooltipText: string;
}) => {
  return (
    <Row>
      <Text.Subheading>{subheadingText}</Text.Subheading>
      <Space width={8} />
      <TextTooltip text={tooltipText} placement='top'>
        <TooltipContainer>
          <Icon source={Icon.CircleInfo} size={16} color={colors.gray.secondary} />
        </TooltipContainer>
      </TextTooltip>
    </Row>
  );
};

const DuplicateAssignedMoversAndJobsSection = ({
  form,
  field,
}: {
  form: DuplicateProjectFormType;
  field: string;
}) => {
  const shouldCopyAssignedMovers = _.get(form.values, `${field}.shouldCopyAssignedMovers`);
  const shouldCopyAssignedTrucks = _.get(form.values, `${field}.shouldCopyAssignedTrucks`);
  return (
    <Row>
      <Space width={24} />
      <Container index={4}>
        <Checkbox
          label={'Duplicate assigned movers'}
          isChecked={shouldCopyAssignedMovers}
          handleToggle={() =>
            form.setFieldValue(`${field}.shouldCopyAssignedMovers`, !shouldCopyAssignedMovers)
          }
        />
        <Space height={8} />
        <Checkbox
          label={'Duplicate assigned trucks'}
          isChecked={shouldCopyAssignedTrucks}
          handleToggle={() =>
            form.setFieldValue(`${field}.shouldCopyAssignedTrucks`, !shouldCopyAssignedTrucks)
          }
        />
      </Container>
    </Row>
  );
};

// Copying Jobs is a prerequisite for copying some modules. This method allows us to reset these job-dependent modules as needed.
const resetJobDepenendentFields = ({
  form,
  field,
}: {
  form: DuplicateProjectFormType;
  field: string;
}) => {
  form.setFieldValue(`${field}.shouldCopyAssignedMovers`, false);
  form.setFieldValue(`${field}.shouldCopyAssignedTrucks`, false);
  form.setFieldValue(`${field}.shouldCopyBilling`, false);
};

const DuplicateProjectDrawerContent = ({
  project,
  handleClose,
}: {
  project: ProjectModel;
  handleClose: () => void;
}) => {
  const responsive = useResponsive();
  const {navigator} = useNavigationDOM();
  const duplicateProjectSuccessToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Project created!',
    actionText: 'View Project',
    handleAction: ({projectUuid}: {projectUuid: string}) =>
      navigator.push(`/projects/${projectUuid}/view`),
  });

  const {form, submitting, handleSubmit} = useDuplicateProjectMutation({
    duplicateProjectForm: DuplicateProjectForm.new(project),
    onSuccess: ({project}: {project: ProjectModel}) => {
      const projectUuid = _.get(project, 'uuid');
      const projectIdentifier = _.get(project, 'identifier');
      duplicateProjectSuccessToast.handleToast({
        actionPayload: {projectUuid},
        message: `Project ${projectIdentifier} created!`,
      });
      handleClose();
    },
    onError: (errors: MutationError[]) => console.log({errors}),
  });
  const field = 'duplicateProjectForm';

  const shouldCopySurvey = _.get(form.values, `${field}.shouldCopySurvey`);
  const shouldCopyJobs = _.get(form.values, `${field}.shouldCopyJobs`);
  const shouldCopyBilling = _.get(form.values, `${field}.shouldCopyBilling`);

  return (
    <React.Fragment>
      <Drawer.Body>
        <DuplicateProjectClientFields
          index={0}
          form={form}
          field={`${field}.projectForm.clientForm`}
          organization={project.organization}
          responsive={responsive}
        />
        <Space height={16} />
        <DuplicateProjectClientFields
          index={1}
          isShowingBillingClient
          form={form}
          field={`${field}.projectForm.billingClientForm`}
          organization={project.organization}
          responsive={responsive}
        />
        <Line />
        <Space height={16} />
        <Container index={2}>
          <Text.Subheading>Survey</Text.Subheading>
          <Space height={8} />
          <Checkbox
            label={'Duplicate survey from current project'}
            isChecked={shouldCopySurvey}
            handleToggle={() => form.setFieldValue(`${field}.shouldCopySurvey`, !shouldCopySurvey)}
          />
        </Container>
        <Space height={16} />
        <Container index={3}>
          <SubheadingWithTooltip
            subheadingText={'Jobs'}
            tooltipText={
              'Office notes, dispatch notes, and crew notes will be cleared when jobs are copied to the new project.'
            }
          />
          <Space height={8} />
          <Checkbox
            label={'Duplicate jobs from current project'}
            isChecked={shouldCopyJobs}
            handleToggle={() => {
              // Note(Hammad): Because setFieldValue is async, clear dependent fields before toggling off shouldCopyJobs to avoid stale values
              if (shouldCopyJobs) {
                resetJobDepenendentFields({form, field});
              }
              form.setFieldValue(`${field}.shouldCopyJobs`, !shouldCopyJobs);
            }}
          />
          {shouldCopyJobs && (
            <React.Fragment>
              <Space height={8} />
              <DuplicateAssignedMoversAndJobsSection form={form} field={field} />
            </React.Fragment>
          )}
        </Container>
        <Space height={16} />
        <Container index={4}>
          <SubheadingWithTooltip
            subheadingText={'Billing'}
            tooltipText={'Includes all bills, bill items, and variables of this project.'}
          />
          <Space height={8} />
          <Checkbox
            label={'Duplicate billing from current project'}
            isDisabled={!shouldCopyJobs}
            isChecked={shouldCopyBilling}
            handleToggle={() => {
              form.setFieldValue(`${field}.shouldCopyBilling`, !shouldCopyBilling);
            }}
          />
        </Container>
      </Drawer.Body>
      {responsive.desktop ? (
        <Drawer.Footer
          isDisabled={submitting}
          isSubmitting={submitting}
          primaryAction={handleSubmit}
          secondaryAction={handleClose}
          primaryActionText={'Duplicate'}
          secondaryActionText={'Cancel'}
        />
      ) : (
        <Drawer.FooterMobile
          isSubmitting={submitting}
          primaryAction={handleSubmit}
          primaryActionText={'Duplicate'}
        />
      )}
    </React.Fragment>
  );
};

const DuplicateProjectDrawerV2 = ({
  projectUuid,
  isOpen,
  handleClose,
}: {
  projectUuid: string;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const responsive = useResponsive();
  const {data, loading} = useQuery(DuplicateProjectDrawerV2.query, {
    variables: {projectUuid},
    skip: !isOpen,
  });

  return (
    <Drawer.PreventPropagationContainer style={{position: 'absolute'}}>
      <Drawer
        isOpen={isOpen}
        handleClose={handleClose}
        width={Drawer.WIDTH.DEFAULT}
        shouldCloseOnClickOutside={false}
      >
        <Loading loading={loading || !data}>
          {() => {
            const {project} = data;
            return (
              <React.Fragment>
                {responsive.desktop ? (
                  <Drawer.Header
                    headerText={`Duplicate ${Project.getName(project)}`}
                    handleClose={handleClose}
                    isClosable={false}
                  />
                ) : (
                  <Drawer.HeaderMobile
                    headerText={`Duplicate ${Project.getName(project)}`}
                    handleClose={handleClose}
                  />
                )}
                <DuplicateProjectDrawerContent project={project} handleClose={handleClose} />
              </React.Fragment>
            );
          }}
        </Loading>
      </Drawer>
    </Drawer.PreventPropagationContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
DuplicateProjectDrawerV2.query = gql`
  ${DuplicateProjectClientFields.fragment}
  ${DuplicateProjectForm.new.fragment}
  ${Project.getName.fragment}
  query DuplicateProjectDrawerV2($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      organization {
        id
        ...DuplicateProjectClientFields
      }
      ...DuplicateProjectForm_new
      ...Project_getName
    }
  }
`;

export default DuplicateProjectDrawerV2;
